import React, { FC } from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';
import { Box, styled } from '@mui/system';
import { Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import { TextField } from '@atoms';
import { SelectFabric } from '@molecules';
import { errorMessage } from '@services';
import defaultImage from '@assets/defaultImage.png';

export interface FormData {
  name: string;
  subdomain?: string;
  logo?: string;
  plan?: string;
  logoFile?: File;
}

const StyledLogoImage = styled('img')({
  height: '95px',
  width: '95px',
  borderRadius: '50%',
  objectFit: 'cover',
});

const StyledDeleteLogoButton = styled('div')({
  height: '32px',
  width: '32px',
  borderRadius: '50%',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  bottom: '5px',
  right: 0,
});

const LabelUploadLogo = styled('label')({
  cursor: 'pointer',
  color: '#1A2332',
  fontSize: '16px',
  border: '1px solid #1A2332',
  padding: '5px 13px',
  borderRadius: '10px',
  fontWeight: 500,
  '&:hover': {
    color: '#ffffffff',
    backgroundColor: '#1A2332',
  },
});

interface CreateEnterPriseAccountFormProps {
  values: FormData;
  control: Control<FormData, any>;
  setValue: UseFormSetValue<FormData>;
}

const CreateEnterPriseAccountForm: FC<CreateEnterPriseAccountFormProps> = ({
  values,
  control,
  setValue,
}) => {
  const onChangeLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (!['png', 'jpg', 'jpeg'].some((type) => file.type.includes(type))) {
      errorMessage('You can only upload jpg or png file.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setValue('logo', e.target.result as string);
        setValue('logoFile', file);
      }
    };
    reader.readAsDataURL(file);
  };

  const onClickDeleteLogo = () => {
    setValue('logo', undefined);
    setValue('logoFile', undefined);
    const fileInput = document.getElementById('upload-logo') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={2}>
        <Box sx={{ position: 'relative' }}>
          <StyledLogoImage src={values.logo || defaultImage} alt="defaultImage" />
          {values.logo && (
            <StyledDeleteLogoButton onClick={onClickDeleteLogo}>
              <DeleteIcon
                fontSize="small"
                sx={{
                  color: '#E53935',
                  '&:hover': {
                    opacity: '0.8',
                  },
                }}
              />
            </StyledDeleteLogoButton>
          )}
        </Box>
        <Box display="flex" alignItems="flex-start" flexDirection="column">
          <Typography variant="h6" mt="8px" color="text.secondary" mb="9px" fontWeight={700}>
            Upload Your Company Logo
          </Typography>
          <LabelUploadLogo htmlFor="upload-logo">
            Upload photo
            <input
              id="upload-logo"
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              hidden
              onChange={onChangeLogo}
            />
          </LabelUploadLogo>
        </Box>
      </Box>
      <TextField
        name="name"
        label="Company name"
        placeholder="Enter company name"
        control={control}
        required
        sx={{ mb: '24px', mt: '24px' }}
      />
      <TextField
        name="subdomain"
        label="Sub domain"
        placeholder="Enter company Subdomain"
        control={control}
        required
        sx={{ mb: '24px' }}
      />
      <SelectFabric
        name="plan"
        label="Subscription Plan"
        placeholder="No plan"
        control={control}
        options={[]}
        sx={{ mb: '24px' }}
      />
    </>
  );
};

export default CreateEnterPriseAccountForm;
